/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQuery, useQueryClient, useMutation } from 'react-query';

import {
  getPartnerContracts,
  saveAssigneeManager,
  updateAssigneeManager,
  saveCertificateIssuance,
  checkCertificateIssuance,
  getFinanceBankCodes,
  savePartnerContractsBankAccount,
  deleteAssigneeManager,
  //
  getPartnerContractCertificateStatus,
  getSalesSettlements,
  getSalesSettlementDetail,
  getSettlementPerSalesHistory,
  getSettlementClientSalesHistory,
  getSettlementClientSalesHistoryByPeriod,
  getSettlementPartnerSalesHistory,
  getSettlementCarryOverSalesHistory,
  getSettlementEvidenceHistory,
  getSettlementDepositTransferHistory,
  createSettlementTaxInvoice,
  downloadSettlementExcel,
  //
  getEvidencesHistories,
  getEvidencesDetail,
  getEvidencesCashReceiptDetail,
  getStoreClientFeesDetail,
  //
  errorHandler
} from 'apis';

export interface TAssigneeManager {
  managerIdx?: number;
  managerName: string;
  managerEmail: string;
  managerPhoneNumber: string;
  isReceivedTaxInvoice: boolean;
}

export interface TPartner {
  address: string;
  phoneNumber: string;
  storeId: string;
  storeName: string;
  storePhone: string;
}

export interface TPartnerContract {
  accountBankAccountHolder: string;
  accountBankAccountNumber: string;
  accountBankCode: string;
  accountBankName: string;
  assigneeManagers: TAssigneeManager[];
  bizAccountIdx: number;
  bizAddress: string;
  bizCategory: string;
  bizEmail: null;
  bizItem: string;
  bizName: string;
  bizNumber: string;
  bizPresidentName: string;
  bizSubNumber: string;
  captainPayCommissionRate: number;
  certificateExpiredDate: null;
  certificateIssuedDate: null;
  contractName: string;
  feeCalcType: 'PER' | 'CLIENT_SUM' | 'PARTNER_SUM';
  feeCalcTypeName: string;
  isEvidenceMerge: true;
  isPayCommissionMerge: true;
  partnerContractIdx: 31;
  partners: TPartner[];
  payDay: string;
  pointCommissionRate: number;
  settlementCycleDay: string;
  taxableEvidenceType: 'ELECTRONIC_CERTIFICATE' | 'ISSUED_BY_PROVIDER' | 'PAPER_TAX_INVOICE';
  taxableEvidenceTypeName: string;
}

export interface TBankAccount {
  bankCode: string;
  bankName: string;
  bankAccount: string;
  // bankAccountNumber: string;
  bankAccountHolder: string;
  bankAccountFile?: unknown;
}

export interface TMutationProps {
  partnerContractIdx: number;
}

export interface TStoreClientFeesDetail {
  companyId?: string;
  companyName?: string;
  distanceMeter?: number;
  pointCommissionRate?: number;
  captainPayCommissionRate?: number;
  paymentPolicy?: string;
  exposureStartDate?: string;
  exposureEndDate?: string;
}

export const useGetPartnerContracts = () => useMutation<TPartnerContract[]>(['getPartnerContracts'], () => getPartnerContracts());

export const useSaveAssigneeManager = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ['saveAssigneeManager'],
    ({ partnerContractIdx, params }: TMutationProps & { params: TAssigneeManager }) => {
      return saveAssigneeManager(partnerContractIdx, params);
    },
    { onSuccess: () => queryClient.invalidateQueries(['getPartnerContracts']) }
  );
};

export const useUpdateAssigneeManager = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ['updateAssigneeManager'],
    ({
      partnerContractIdx,
      assigneeManagerIdx,
      params
    }: TMutationProps & { assigneeManagerIdx: number; params: TAssigneeManager }) => {
      return updateAssigneeManager(partnerContractIdx, assigneeManagerIdx, params);
    },
    { onSuccess: () => queryClient.invalidateQueries(['getPartnerContracts']) }
  );
};

export const useSaveCertificateIssuance = () => {
  return useMutation(
    ['saveCertificateIssuance'],
    ({ partnerContractIdx, bizAccountIdx }: TMutationProps & { bizAccountIdx: number }) =>
      saveCertificateIssuance(partnerContractIdx, { bizAccountIdx }),
    { onError: errorHandler }
  );
};

export const useCheckCertificateIssuance = (onSuccess: (values: any) => void, onError: (error: any) => void) => {
  return useMutation(
    ['checkCertificateIssuance'],
    (params: { partnerContractIdx: number; bizAccountIdx: number }) => checkCertificateIssuance(params),
    { onSuccess, onError }
  );
};

export const useGetFinanceBankCodes = () => {
  return useQuery<{ bankCode: string; bankName: string }[]>(['getFinanceBankCodes'], () => getFinanceBankCodes());
};

export const useSavePartnerContractsBankAccount = () => {
  return useMutation(
    ['savePartnerContractsBankAccount'],
    ({ partnerContractIdx, params }: TMutationProps & { params: FormData }) => {
      return savePartnerContractsBankAccount(partnerContractIdx, params);
    }
  );
};

export const useDeleteAssigneeManager = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ['deleteAssigneeManager'],
    ({ partnerContractIdx, assigneeManagerIdx }: TMutationProps & { assigneeManagerIdx: number }) => {
      return deleteAssigneeManager(partnerContractIdx, assigneeManagerIdx);
    },
    { onSuccess: () => queryClient.invalidateQueries(['getPartnerContracts']) }
  );
};

// 매출 정산

// 제휴점 정산서 공동인증서 유효성 확인
export const useGetPartnerContractCertificateStatus = (settlementIdx: string) => {
  return useMutation(['getPartnerContractCertificateStatus'], () => {
    return getPartnerContractCertificateStatus(settlementIdx);
  });
};
// 정산서 목록 조회
export const useGetSalesSettlements = (params: unknown) => {
  return useQuery(['getSalesSettlements', params], () => getSalesSettlements(params));
};
// 정산서 조회
export const useGetSalesSettlementDetail = (settlementIdx: string) => {
  return useMutation(['getSalesSettlementDetail', settlementIdx], () => getSalesSettlementDetail(settlementIdx));
};

// 정산서 매출 내역 조회 (거래 건별)
export const useGetSettlementPerSalesHistory = (settlementIdx: string) => {
  return useMutation(['getSettlementPerSalesHistory'], (params: unknown) => {
    return getSettlementPerSalesHistory(settlementIdx, params);
  });
};

// 정산서 매출 내역 조회 (회사 별)
export const useGetSettlementClientSalesHistory = (settlementIdx: string) => {
  return useMutation(['getSettlementClientSalesHistory'], () => getSettlementClientSalesHistory(settlementIdx));
};

// 정산서 매출 내역 조회 (회사 별 / 기간 필터)
export const useGetSettlementClientSalesHistoryByPeriod = (settlementIdx: string) => {
  return useMutation(
    ['getSettlementClientSalesHistoryByPeriod'],
    (body: { paymentStartDate: string; paymentEndDate: string; partnerContractIdxes?: number[] }) =>
      getSettlementClientSalesHistoryByPeriod(settlementIdx, body)
  );
};

// 정산서 매출 내역 조회 (제휴점 별)
export const useGetSettlementPartnerSalesHistory = (settlementIdx: string) => {
  return useMutation(['getSettlementPartnerSalesHistory'], () => getSettlementPartnerSalesHistory(settlementIdx));
};

// 정산서 매출 내역 조회 (이월 거래내역)
export const useGetSettlementCarryOverSalesHistory = (settlementIdx: string) => {
  return useMutation(['getSettlementCarryOverSalesHistory'], () => getSettlementCarryOverSalesHistory(settlementIdx));
};

// 정산서 증빙 내역 조회
export const useGetSettlementEvidenceHistory = (settlementIdx: string) => {
  return useMutation(['getSettlementEvidenceHistory'], () => getSettlementEvidenceHistory(settlementIdx));
};

// 정산서 상세 엑셀 다운로드 요청
export const useDownloadSettlementExcel = () => {
  return useMutation(['downloadSettlementExcel'], ({ settlementIdx, sid }: { settlementIdx: string; sid: string }) =>
    downloadSettlementExcel(settlementIdx, sid)
  );
};

// 정산서 입금/이체 내역 조회
export const useGetSettlementDepositTransferHistory = (settlementIdx: string) => {
  return useMutation(['getSettlementDepositTransferHistory'], () => getSettlementDepositTransferHistory(settlementIdx));
};

// 정산서 세금계산서 발행
export const useCreateSettlementTaxInvoice = (settlementIdx: string) => {
  return useMutation(['createSettlementTaxInvoice'], () => createSettlementTaxInvoice(settlementIdx));
};

export const useGetEvidencesHistories = (params: unknown) => {
  return useQuery(['getEvidencesHistories', params], () => getEvidencesHistories(params));
};

export const useGetEvidencesDetail = () => {
  return useMutation(['getEvidencesDetail'], (evidenceIdx: string | number) => getEvidencesDetail(evidenceIdx));
};

export const useGetEvidencesCashReceiptDetail = () => {
  return useMutation(['getEvidencesCashReceiptDetail'], (evidenceIdx: string | number) => {
    return getEvidencesCashReceiptDetail(evidenceIdx);
  });
};

// 고객사 확인
export const useGetStoreClientFeesDetail = (storeId: string) => {
  return useQuery<TStoreClientFeesDetail[]>({
    queryKey: ['getStoreClientFeesDetail', storeId],
    queryFn: () => getStoreClientFeesDetail(storeId),
    enabled: !!storeId
  });
};
